import { useEffect, useMemo, useState, type FC } from "react";
import { Link } from "react-router-dom";
import {
  useCustomers,
  fetchCustomers,
  createCustomer,
  setCustomers,
} from "../stores/customer_store";
import type { Customer } from "../../../shared/models/customer";

export const Admin: FC = () => {
  const customers = useCustomers();
  const [error, setError] = useState<string | null>(null);
  const [createName, setCreateName] = useState("");
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);

  useEffect(() => {
    fetchCustomers().catch((err: unknown) => {
      const msg =
        err instanceof Error ? err.message : "Failed to load customers";
      setError(msg);
      // Set empty array so loading state resolves and error UI can render
      setCustomers([]);
    });
  }, []);

  const sortedCustomers = useMemo(() => {
    if (!customers) return [];
    return [...customers].sort((a, b) => a.name.localeCompare(b.name));
  }, [customers]);

  const handleCopy = async (customerId: string) => {
    try {
      await navigator.clipboard.writeText(customerId);
      setCopiedId(customerId);
      setTimeout(() => setCopiedId(null), 2000);
    } catch {
      // Clipboard API not available — ignore silently
    }
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = createName.trim();
    if (!trimmed) return;

    setCreating(true);
    setCreateError(null);
    try {
      await createCustomer(trimmed);
      setCreateName("");
    } catch (err: unknown) {
      const msg =
        err instanceof Error ? err.message : "Failed to create customer";
      setCreateError(msg);
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="vmx-page">
      <div className="vmx-page-header">
        <div>
          <div className="vmx-page-title">👤 Admin Portal</div>
          <div className="vmx-page-subtitle">
            Manage customers and user assignments.
          </div>
        </div>
      </div>

      {/* Loading state */}
      {!customers && !error && (
        <div className="vmx-card" style={{ textAlign: "center", padding: 48 }}>
          <div style={{ color: "var(--vmx-gray-400)", fontSize: "0.875rem" }}>
            Loading customers…
          </div>
        </div>
      )}

      {/* Create Customer Form */}
      {customers && (
        <div className="vmx-card" style={{ marginBottom: 24 }}>
          <h3 style={{ margin: "0 0 12px", fontSize: 16, fontWeight: 600 }}>
            Create Customer
          </h3>
          <form
            onSubmit={handleCreate}
            style={{ display: "flex", gap: 12, alignItems: "flex-start" }}
          >
            <input
              className="vmx-input"
              type="text"
              placeholder="Customer name"
              value={createName}
              onChange={(e) => setCreateName(e.target.value)}
              disabled={creating}
              style={{ flex: 1, maxWidth: 400 }}
            />
            <button
              className="vmx-btn vmx-btn-primary"
              type="submit"
              disabled={creating || createName.trim() === ""}
            >
              {creating ? "Creating…" : "＋ Create Customer"}
            </button>
          </form>
          {createError && (
            <div
              className="vmx-error-message"
              style={{ marginTop: 8, color: "var(--vmx-danger)" }}
            >
              {createError}
            </div>
          )}
        </div>
      )}

      {/* Error state */}
      {error && (
        <div className="vmx-card">
          <div className="vmx-empty">
            <div className="vmx-empty-icon">⚠</div>
            <div className="vmx-empty-title">Failed to load customers</div>
            <div className="vmx-empty-text">{error}</div>
            <button
              className="vmx-btn vmx-btn-primary"
              onClick={() => {
                setError(null);
                fetchCustomers().catch((err: unknown) => {
                  const msg =
                    err instanceof Error
                      ? err.message
                      : "Failed to load customers";
                  setError(msg);
                  setCustomers([]);
                });
              }}
            >
              Retry
            </button>
          </div>
        </div>
      )}

      {/* Empty state */}
      {!error && customers && sortedCustomers.length === 0 && (
        <div className="vmx-card">
          <div className="vmx-empty">
            <div className="vmx-empty-icon">👤</div>
            <div className="vmx-empty-title">No customers yet</div>
            <div className="vmx-empty-text">
              Create your first customer to get started.
            </div>
          </div>
        </div>
      )}

      {/* Customer list */}
      {!error && sortedCustomers.length > 0 && (
        <div className="vmx-table-wrapper vmx-card-table-wrapper">
          <table className="vmx-table vmx-card-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Customer ID</th>
              </tr>
            </thead>
            <tbody>
              {sortedCustomers.map((c: Customer) => (
                <tr key={c.customerId}>
                  <td data-label="Name">
                    <Link
                      to={`/admin/customers/${c.customerId}`}
                      style={{
                        fontWeight: 600,
                        color: "var(--vmx-accent)",
                        textDecoration: "none",
                      }}
                    >
                      {c.name}
                    </Link>
                  </td>
                  <td data-label="Customer ID">
                    <button
                      className="vmx-btn vmx-btn-ghost vmx-btn-sm"
                      style={{
                        fontFamily: "monospace",
                        fontSize: 13,
                        color:
                          copiedId === c.customerId
                            ? "var(--vmx-success)"
                            : "var(--vmx-gray-600)",
                        padding: "2px 6px",
                      }}
                      onClick={() => handleCopy(c.customerId)}
                      title="Click to copy"
                    >
                      {copiedId === c.customerId ? "✓ Copied!" : c.customerId}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
